import React from 'react';
import { Box, GlobalStyles } from '@mui/material';
import { AssetLabel, LABEL_HEIGHT_IN, LABEL_WIDTH_IN, type LabelAsset } from './AssetLabel';

interface LabelSheetProps {
  assets: LabelAsset[];
  /** 1-based position on the 48-cell sheet where printing starts (T129) — cells before it print
   * blank, so a partially used sheet isn't reprinted over (FR-015, SC-013). */
  startPosition: number;
  /** T129's "print alignment test" action — same 4x12 grid, outlined cells only, no real QR/text,
   * for verifying registration on plain paper before committing a real label sheet. */
  outlineOnly?: boolean;
}

export const COLUMNS = 4;
export const ROWS = 12;
export const TOTAL_CELLS = COLUMNS * ROWS; // 48 — one full OL1735LP sheet

// OL1735LP sheet geometry (research.md item 8): US Letter, 0.25" margins on all sides, 0.333"
// horizontal / 0.136" vertical gaps between labels. Confirmed by arithmetic:
// 0.25 + 4(1.75) + 3(0.333) + 0.25 ~= 8.5", 0.25 + 12(0.75) + 11(0.136) + 0.25 ~= 11.0".
const MARGIN_IN = 0.25;
const GAP_H_IN = 0.333;
const GAP_V_IN = 0.136;

// One cell per sheet position — null for a blank cell (before `startPosition` or past the last
// selected asset).
function buildCells(assets: LabelAsset[], startPosition: number): (LabelAsset | null)[] {
  return Array.from({ length: TOTAL_CELLS }, (_, position) => {
    const assetIndex = position - (startPosition - 1);
    return assetIndex >= 0 && assetIndex < assets.length ? assets[assetIndex] : null;
  });
}

// `@page`/print CSS (research.md item 8's "browser print-scaling risk"): Chrome/Edge default to
// non-zero page margins and a "fit to page" scale, which would shift this grid and ruin
// registration against die-cut labels. `size: letter; margin: 0` plus the sheet's own 0.25"
// padding (not the browser's page margin) is what keeps the grid's physical dimensions accurate
// when printed at 100% scale with the browser's own margins set to none.
const PRINT_STYLES = `
  @page { size: letter; margin: 0; }
  @media print {
    body * { visibility: hidden; }
    .label-sheet, .label-sheet * { visibility: visible; }
    .label-sheet { position: absolute; top: 0; left: 0; }
  }
`;

/**
 * The OL1735LP label sheet (T070): a 4x12 grid of `AssetLabel`s, laid out from the sheet's own
 * geometry, with blank cells before `startPosition` so a partially used sheet can be reloaded
 * without duplicating already-printed labels (FR-015).
 */
export const LabelSheet: React.FC<LabelSheetProps> = ({ assets, startPosition, outlineOnly = false }) => {
  const cells = buildCells(assets, startPosition);

  return (
    <>
      <GlobalStyles styles={PRINT_STYLES} />
      <Box
        className="label-sheet"
        sx={{
          width: '8.5in',
          minHeight: '11in',
          padding: `${MARGIN_IN}in`,
          boxSizing: 'border-box',
          display: 'grid',
          gridTemplateColumns: `repeat(${COLUMNS}, ${LABEL_WIDTH_IN}in)`,
          gridAutoRows: `${LABEL_HEIGHT_IN}in`,
          columnGap: `${GAP_H_IN}in`,
          rowGap: `${GAP_V_IN}in`,
          backgroundColor: '#fff',
        }}
      >
        {cells.map((asset, position) => (
          <Box
            key={position}
            data-testid={`label-cell-${position}`}
            sx={outlineOnly ? { border: '1px dashed #94a3b8', boxSizing: 'border-box' } : undefined}
          >
            {!outlineOnly && asset && <AssetLabel asset={asset} />}
          </Box>
        ))}
      </Box>
    </>
  );
};

export default LabelSheet;
